import { BN, ZERO } from '@drift-labs/sdk';
import { useMemo } from 'react';
import { TradeFormMessage } from 'src/@types/types';

interface UseCrossMarginValidationForIsolatedDepositParams {
	/**
	 * Total margin required for opening an isolated position including any existing isolated margin shortfalls.
	 */
	totalMarginRequired: BN | undefined;
	/**
	 * Free collateral available in the cross account.
	 */
	freeCollateral: BN;
	/**
	 * Margin mode of the trade.
	 */
	marginMode: 'cross' | 'isolated';
}

/**
 * Hook that validates whether an isolated position deposit would cause cross positions
 * to fail margin requirements.
 *
 * When adding size to an isolated position, collateral is transferred from the cross
 * account. If this transfer exceeds the free collateral, cross positions will fail
 * initial margin requirements.
 *
 * @returns TradeFormMessage object with error details, or undefined if no validation error
 */
export function useCrossMarginValidationForIsolatedDeposit({
	totalMarginRequired,
	freeCollateral,
	marginMode,
}: UseCrossMarginValidationForIsolatedDepositParams):
	| TradeFormMessage
	| undefined {
	return useMemo(() => {
		// Only validate for isolated margin mode
		if (marginMode !== 'isolated') {
			return undefined;
		}

		// No margin required, no validation needed
		if (!totalMarginRequired || totalMarginRequired.lte(ZERO)) {
			return undefined;
		}

		// Check if required margin would exceed free collateral
		const wouldExceedFreeCollateral = totalMarginRequired.gt(freeCollateral);

		if (wouldExceedFreeCollateral) {
			return {
				messageTitle: '',
				shouldShowMessage: true,
				message: 'Cross positions will fail margin check. Try a smaller size.',
				messageType: 'error',
				skipMessageInConfirmationModal: true,
				disableTradeButton: true,
			};
		}

		return undefined;
	}, [totalMarginRequired, freeCollateral, marginMode]);
}
